/*                                                               */
/*                                               */
import { hydrate_next, hydrate_node, hydrating, set_hydrate_node } from './hydration.js';
import {
	create_text,
	get_first_child,
	get_next_sibling,
	is_firefox,
	create_element,
	create_fragment,
	create_comment,
	set_attribute,
	merge_text_nodes
} from './operations.js';
import { create_fragment_from_html } from './reconciler.js';
import { active_effect } from '../runtime.js';
import {
	NAMESPACE_MATHML,
	NAMESPACE_SVG,
	TEMPLATE_FRAGMENT,
	TEMPLATE_USE_IMPORT_NODE,
	TEMPLATE_USE_MATHML,
	TEMPLATE_USE_SVG
} from '../../../constants.js';
import {
	COMMENT_NODE,
	DOCUMENT_FRAGMENT_NODE,
	IS_XHTML,
	REACTION_RAN,
	TEXT_NODE
} from '#client/constants';

const TEMPLATE_TAG = IS_XHTML ? 'template' : 'TEMPLATE';
const SCRIPT_TAG = IS_XHTML ? 'script' : 'SCRIPT';

/**
 *
 *
 */
export function assign_nodes(start, end) {
	var effect = /*                 */ (active_effect);
	if (effect.nodes === null) {
		effect.nodes = { start, end, a: null, t: null };
	}
}

/**
 *
 *
 *
 */
/*                    */
export function from_html(content, flags) {
	var is_fragment = (flags & TEMPLATE_FRAGMENT) !== 0;
	var use_import_node = (flags & TEMPLATE_USE_IMPORT_NODE) !== 0;

	/*               */
	var node;

	/**
 *
 *
 */
	var has_start = !content.startsWith('<!>');

	return () => {
		if (hydrating) {
			assign_nodes(hydrate_node, null);
			return hydrate_node;
		}

		if (node === undefined) {
			node = create_fragment_from_html(has_start ? content : '<!>' + content);
			if (!is_fragment) node = /*                       */ (get_first_child(node));
		}

		var clone = /*                       */ (
			use_import_node || is_firefox ? document.importNode(node, true) : node.cloneNode(true)
		);

		if (is_fragment) {
			var start = /*                       */ (get_first_child(clone));
			var end = /*                       */ (clone.lastChild);

			assign_nodes(start, end);
		} else {
			assign_nodes(clone, clone);
		}

		return clone;
	};
}

/**
 *
 *
 *
 *
 */
/*                    */
function from_namespace(content, flags, ns = 'svg') {
	/**
 *
 *
 */
	var has_start = !content.startsWith('<!>');

	var is_fragment = (flags & TEMPLATE_FRAGMENT) !== 0;
	var wrapped = `<${ns}>${has_start ? content : '<!>' + content}</${ns}>`;

	/*                                     */
	var node;

	return () => {
		if (hydrating) {
			assign_nodes(hydrate_node, null);
			return hydrate_node;
		}

		if (!node) {
			var fragment = /*                           */ (create_fragment_from_html(wrapped));
			var root = /*                  */ (get_first_child(fragment));

			if (is_fragment) {
				node = document.createDocumentFragment();
				while (get_first_child(root)) {
					node.appendChild(/*                       */ (get_first_child(root)));
				}
			} else {
				node = /*                  */ (get_first_child(root));
			}
		}

		var clone = /*                       */ (node.cloneNode(true));

		if (is_fragment) {
			var start = /*                       */ (get_first_child(clone));
			var end = /*                       */ (clone.lastChild);

			assign_nodes(start, end);
		} else {
			assign_nodes(clone, clone);
		}

		return clone;
	};
}

/**
 *
 *
 */
/*                    */
export function from_svg(content, flags) {
	return from_namespace(content, flags, 'svg');
}

/**
 *
 *
 */
/*                    */
export function from_mathml(content, flags) {
	return from_namespace(content, flags, 'math');
}

/**
 *
 *
 */
function fragment_from_tree(structure, ns) {
	var fragment = create_fragment();

	for (var item of structure) {
		if (typeof item === 'string') {
			fragment.append(create_text(item));
			continue;
		}

		/*                                                                          */
		if (item === undefined || item[0][0] === '/') {
			fragment.append(create_comment(item ? item[0].slice(3) : ''));
			continue;
		}

		const [name, attributes, ...children] = item;

		const namespace = name === 'svg' ? NAMESPACE_SVG : name === 'math' ? NAMESPACE_MATHML : ns;

		var element = create_element(name, namespace, attributes?.is);

		for (var key in attributes) {
			set_attribute(element, key, attributes[key]);
		}

		if (children.length > 0) {
			var target =
				element.nodeName === TEMPLATE_TAG
					? /*                              */ (element).content
					: element;

			target.append(
				fragment_from_tree(children, element.nodeName === 'foreignObject' ? undefined : namespace)
			);
		}

		fragment.append(element);
	}

	return fragment;
}

/**
 *
 *
 *
 */
/*                    */
export function from_tree(structure, flags) {
	var is_fragment = (flags & TEMPLATE_FRAGMENT) !== 0;
	var use_import_node = (flags & TEMPLATE_USE_IMPORT_NODE) !== 0;

	/*               */
	var node;

	return () => {
		if (hydrating) {
			assign_nodes(hydrate_node, null);
			return hydrate_node;
		}

		if (node === undefined) {
			const ns =
				(flags & TEMPLATE_USE_SVG) !== 0
					? NAMESPACE_SVG
					: (flags & TEMPLATE_USE_MATHML) !== 0
						? NAMESPACE_MATHML
						: undefined;

			node = fragment_from_tree(structure, ns);
			if (!is_fragment) node = /*                       */ (get_first_child(node));
		}

		var clone = /*                       */ (
			use_import_node || is_firefox ? document.importNode(node, true) : node.cloneNode(true)
		);

		if (is_fragment) {
			var start = /*                       */ (get_first_child(clone));
			var end = /*                       */ (clone.lastChild);

			assign_nodes(start, end);
		} else {
			assign_nodes(clone, clone);
		}

		return clone;
	};
}

/**
 *
 */
export function with_script(fn) {
	return () => run_scripts(fn());
}

/**
 *
 *
 *
 *
 */
function run_scripts(node) {
	/*                                              */
	if (hydrating) return node;

	const is_fragment = node.nodeType === DOCUMENT_FRAGMENT_NODE;
	const scripts =
		/*                      */ (node).nodeName === SCRIPT_TAG
			? [/*                            */ (node)]
			: node.querySelectorAll('script');

	const effect = /*                                          */ (active_effect);

	for (const script of scripts) {
		const clone = create_element('script');
		for (var attribute of script.attributes) {
			clone.setAttribute(attribute.name, attribute.value);
		}

		clone.textContent = script.textContent;

		/*                                                                                 */
		if (is_fragment ? node.firstChild === script : node === script) {
			effect.nodes.start = clone;
		}
		if (is_fragment ? node.lastChild === script : node === script) {
			effect.nodes.end = clone;
		}

		script.replaceWith(clone);
	}
	return node;
}

/**
 *
 *
 */
export function text(value = '') {
	if (!hydrating) {
		var t = create_text(value + '');
		assign_nodes(t, t);
		return t;
	}

	var node = hydrate_node;

	if (node.nodeType !== TEXT_NODE) {
		/*                                                                           */
		node.before((node = create_text()));
		set_hydrate_node(node);
	} else {
		merge_text_nodes(/*               */ (node));
	}

	assign_nodes(node, node);
	return node;
}

/**
 *
 */
export function comment() {
	/*                                                              */
	if (hydrating) {
		assign_nodes(hydrate_node, null);
		return hydrate_node;
	}

	var frag = document.createDocumentFragment();
	var start = document.createComment('');
	var anchor = create_text();
	frag.append(start, anchor);

	assign_nodes(start, anchor);

	return frag;
}

/**
 *
 *
 *
 *
 */
export function append(anchor, dom) {
	if (hydrating) {
		var effect = /*                                          */ (active_effect);

		/*                                                                                             */
		/*                                                                                            */
		/*                                                                                                     */
		if ((effect.f & REACTION_RAN) === 0 || effect.nodes.end === null) {
			effect.nodes.end = hydrate_node;
		}

		hydrate_next();
		return;
	}

	if (anchor === null) {
		/*                                               */
		return;
	}

	anchor.before(/*               */ (dom));
}

/**
 *
 */
export function props_id() {
	if (
		hydrating &&
		hydrate_node &&
		hydrate_node.nodeType === COMMENT_NODE &&
		hydrate_node.textContent?.startsWith(`$`)
	) {
		const id = hydrate_node.textContent.substring(1);
		hydrate_next();
		return id;
	}

	/*                                                                               */
	(window.__svelte ??= {}).uid ??= 1;

	/*               */
	return `c${window.__svelte.uid++}`;
}
